import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  cancelBroadcast,
  createBroadcast,
  fetchBroadcast,
  fetchBroadcasts,
  previewBroadcastAudience,
  sendBroadcast,
} from '@/api/broadcasts/broadcastsApi';
import type {
  AudiencePreview,
  Broadcast,
  BroadcastAudienceKind,
  BroadcastPurpose,
  BroadcastDetail,
  CreateBroadcastPayload,
} from '@/api/broadcasts/broadcastsApi.types';
import type { PaginatedResult } from '@/api/pagination.types';
import { ADMIN_LIST_PAGE_SIZE } from '@/constants/adminPagination';
import { BROADCASTS_QUERY_KEY } from '@/constants/queryKeys';

export function useBroadcastsQuery(page: number): UseQueryResult<PaginatedResult<Broadcast>, Error> {
  return useQuery({
    queryKey: [...BROADCASTS_QUERY_KEY, 'list', page],
    queryFn: function loadBroadcasts() {
      return fetchBroadcasts(page, ADMIN_LIST_PAGE_SIZE);
    },
    placeholderData: keepPreviousData,
  });
}

export function useBroadcastQuery(
  broadcastId: string
): UseQueryResult<BroadcastDetail, Error> {
  return useQuery({
    queryKey: [...BROADCASTS_QUERY_KEY, 'detail', broadcastId],
    queryFn: function loadBroadcast() {
      return fetchBroadcast(broadcastId);
    },
  });
}

export function useCreateBroadcastMutation(): UseMutationResult<
  Broadcast,
  Error,
  CreateBroadcastPayload
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createBroadcast,
    onSuccess: function invalidate() {
      void queryClient.invalidateQueries({ queryKey: BROADCASTS_QUERY_KEY });
    },
  });
}

export interface SendBroadcastInput {
  broadcastId: string;
  scheduledFor: string | null;
}

export function useSendBroadcastMutation(): UseMutationResult<Broadcast, Error, SendBroadcastInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function submitSend(input: SendBroadcastInput) {
      return sendBroadcast(input.broadcastId, input.scheduledFor);
    },
    onSuccess: function invalidate() {
      void queryClient.invalidateQueries({ queryKey: BROADCASTS_QUERY_KEY });
    },
  });
}

export function useCancelBroadcastMutation(): UseMutationResult<Broadcast, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelBroadcast,
    onSuccess: function invalidate() {
      void queryClient.invalidateQueries({ queryKey: BROADCASTS_QUERY_KEY });
    },
  });
}

export interface AudiencePreviewInput {
  audienceKind: BroadcastAudienceKind;
  purpose: BroadcastPurpose;
  selectedClientIds: string[] | null;
}

export function usePreviewBroadcastAudienceMutation(): UseMutationResult<
  AudiencePreview,
  Error,
  AudiencePreviewInput
> {
  return useMutation({
    mutationFn: function submitPreview(input: AudiencePreviewInput) {
      return previewBroadcastAudience(input.audienceKind, input.purpose, input.selectedClientIds);
    },
  });
}
